import { clsx } from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useGoogleMaps } from '../hooks/useGoogleMaps';

export interface AddressComponents {
  streetAddress: string;
  streetAddress2?: string;
  city: string;
  state: string;
  zipCode: string;
  fullAddress: string;
}

interface AddressAutocompleteProps {
  id?: string;
  /** Playwright / test hook for the visible text input */
  inputTestId?: string;
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (addressComponents: AddressComponents) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  data1pIgnore?: boolean;
}

/**
 * Address autocomplete component using Google Places API
 * Provides address suggestions as the user types and auto-fills address fields when selected
 *
 * This component is intended to be used as one of a set of address fields.
 * The `onPlaceSelected` callback should handle updating the additional address fields:
 *
 * @example
 * <AddressAutocomplete
 *   value={streetAddress}
 *   onChange={(value) => setStreetAddress(value)}
 *   onPlaceSelected={(addressComponents) => {
 *     setStreetAddress(addressComponents.streetAddress);
 *     setStreetAddress2(addressComponents.streetAddress2 || '');
 *     setCity(addressComponents.city);
 *     setState(addressComponents.state);
 *     setZipCode(addressComponents.zipCode);
 *   }}
 * />
 *
 * Note: The component will show a deprecation warning but continues to work reliably
 */
export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  id,
  inputTestId,
  value,
  onChange,
  onPlaceSelected,
  placeholder = 'Street Address',
  className,
  disabled = false,
  data1pIgnore = false,
}) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const inputRef = useRef<HTMLInputElement>(null);
  // TODO: this will eventually need to be replaced with google.maps.places.PlaceAutocompleteElement
  // https://developers.google.com/maps/documentation/javascript/reference/places-widget#PlaceAutocompleteElement
  // this^ is a web component that will be cumbersome to use in React and style according to our design system (as of 10/2025)
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [inputValue, setInputValue] = useState(value);

  // Use refs to store the latest callback functions to avoid re-initializing autocomplete
  const onChangeRef = useRef(onChange);
  const onPlaceSelectedRef = useRef(onPlaceSelected);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onPlaceSelectedRef.current = onPlaceSelected;
  }, [onPlaceSelected]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Add custom styles for the Google Places dropdown to make it wider
  useEffect(() => {
    if (!isLoaded) return;

    const styleId = 'google-places-dropdown-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .pac-container {
          min-width: 400px !important;
          max-width: 600px !important;
          width: auto !important;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1) !important;
          border-radius: 0.5rem !important;
          margin-top: 0.25rem !important;
        }
        .pac-item {
          padding: 8px 12px !important;
          line-height: 1.5 !important;
        }
        .pac-item-query {
          font-size: 14px !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, [isLoaded]);

  const parseAddressComponents = useCallback(
    (place: google.maps.places.PlaceResult): AddressComponents => {
      const components = place.address_components || [];
      let streetNumber = '';
      let route = '';
      let subpremise = '';
      let city = '';
      let state = '';
      let zipCode = '';

      components.forEach((component) => {
        const types = component.types;
        if (types.includes('street_number')) {
          streetNumber = component.long_name;
        } else if (types.includes('route')) {
          route = component.long_name;
        } else if (types.includes('subpremise')) {
          subpremise = component.long_name;
        } else if (types.includes('locality')) {
          city = component.long_name;
        } else if (types.includes('administrative_area_level_1')) {
          state = component.short_name;
        } else if (types.includes('postal_code')) {
          zipCode = component.long_name;
        }
      });

      const streetAddress = `${streetNumber} ${route}`.trim();

      return {
        streetAddress,
        streetAddress2: subpremise,
        city,
        state,
        zipCode,
        fullAddress: place.formatted_address || '',
      };
    },
    []
  );

  useEffect(() => {
    if (!isLoaded || !inputRef.current || !window.google || disabled) return;

    try {
      // TODO: this will eventually need to be replaced with google.maps.places.PlaceAutocompleteElement
      // https://developers.google.com/maps/documentation/javascript/reference/places-widget#PlaceAutocompleteElement
      // this^ is a web component that will be cumbersome to use in React and style according to our design system (as of 10/2025)
      autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address'],
        types: ['address'],
      });

      const listener = autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current?.getPlace();

        if (place && place.address_components) {
          const addressComponents = parseAddressComponents(place);
          setInputValue(addressComponents.streetAddress);
          // Don't call onChange here - onPlaceSelected will handle all updates
          // Only call onChange when user manually types (see handleInputChange)
          onPlaceSelectedRef.current(addressComponents);
        }
      });

      return () => {
        if (listener) {
          google.maps.event.removeListener(listener);
        }
        if (autocompleteRef.current) {
          google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }
      };
    } catch (error) {
      console.error('Failed to initialize Google Places Autocomplete:', error);
    }
  }, [isLoaded, disabled, parseAddressComponents]);

  // Prevent Enter key from submitting the form when Google Places dropdown is visible
  useEffect(() => {
    if (!inputRef.current) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        const pacContainer = document.querySelector('.pac-container');
        const isDropdownVisible =
          pacContainer && (pacContainer as HTMLElement).offsetParent !== null;

        if (isDropdownVisible) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    const input = inputRef.current;
    input.addEventListener('keydown', handleKeyDown);

    return () => {
      input.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChangeRef.current(newValue);
  };

  if (loadError) {
    console.error('Google Maps API failed to load:', loadError);
  }

  return (
    <span
      data-slot="control"
      className={clsx([
        className,
        'tw:relative tw:block tw:w-full',
        // Background styling
        'tw:before:absolute tw:before:inset-px tw:before:rounded-[calc(theme(borderRadius.lg)-1px)] tw:before:bg-white tw:before:shadow',
        'tw:dark:before:hidden',
        // Focus ring
        'tw:after:pointer-events-none tw:after:absolute tw:after:inset-0 tw:after:rounded-lg tw:after:ring-inset tw:after:ring-transparent tw:sm:after:focus-within:ring-2 tw:sm:after:focus-within:ring-brand-green-0',
        // Disabled state
        'tw:has-[[data-disabled]]:opacity-50 tw:before:has-[[data-disabled]]:bg-zinc-950/5 tw:before:has-[[data-disabled]]:shadow-none',
      ])}
    >
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        placeholder={!isLoaded ? 'Loading...' : placeholder}
        disabled={disabled || !isLoaded}
        data-1p-ignore={data1pIgnore}
        data-testid={inputTestId}
        autoComplete="off"
        className={clsx([
          // Layout
          'tw:relative tw:block tw:w-full tw:appearance-none tw:rounded-lg',
          'tw:px-[calc(theme(spacing[3.5])-1px)] tw:py-[calc(theme(spacing[2.5])-1px)]',
          'tw:sm:px-[calc(theme(spacing[3])-1px)] tw:sm:py-[calc(theme(spacing[1.5])-1px)]',
          // Typography
          'tw:text-base/6 tw:text-zinc-950 tw:placeholder:text-zinc-500',
          'tw:sm:text-sm/6 tw:dark:text-white',
          // Border
          'tw:border tw:border-zinc-950/10 hover:tw:border-zinc-950/20',
          'tw:dark:border-white/10 tw:dark:hover:border-white/20',
          // Background
          'tw:bg-transparent tw:dark:bg-white/5',
          // Focus
          'tw:focus:outline-none',
          // Disabled
          'disabled:tw:border-zinc-950/20 disabled:tw:opacity-50',
          'disabled:tw:dark:border-white/15 disabled:tw:dark:bg-white/[2.5%]',
          // Color scheme
          'tw:dark:[color-scheme:dark]',
        ])}
      />
    </span>
  );
};
